/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import validate from "../util/validate.js";
import util from "../util/util.js";
import moment from "dayjs";
import googleMaps from "../util/google-maps.js";
import mapStyles from "../map/styles";
import mapFeatures from "../map/features";

import { observePromise, flatMapComputed } from '../util/ko-util';

import '../util/custom-bindings.js';
import MediaUploaderViewModel from '../ui/shared/media-uploader.js';
import bibyProgram from '../programs/biby.js';

var RANGE_CORE = 1;
var RANGE_UNKNOWN = 1.5;
var RANGE_SUSPECT = 2;
var RANGE_VAGRANT = 3;
var RANGE_HISTORICAL = 4;
var RANGE_IRRUPTIVE = 5;
var RANGE_INTRODUCED = 6;
var RANGE_ESCAPED = 8;
var RANGE_ULTRA_VAGRANT = 9;
var RANGE_OUT_OF_RANGE = 10;

function rangeDescription(range, long) {
	if(range == RANGE_CORE || range == RANGE_ESCAPED) {
		return "Species within current estimated core range";
	} else if(range == RANGE_VAGRANT || range == RANGE_ULTRA_VAGRANT) {
		return "Species within current estimated vagrant range";
	} else if(range == RANGE_IRRUPTIVE) {
		return "Species within current estimated irruptive range";
	} else if(range == RANGE_INTRODUCED) {
		return "Species within current estimated introduced range";
	} else if(range == RANGE_OUT_OF_RANGE || range == RANGE_SUSPECT || range == RANGE_HISTORICAL) {
		if(long) {
			return "You are entering a species that appears to be out of range, proceed only if confident. You may add detail of your sighting in 'Notes'.";
		} else {
			return "Species out of current estimated range";
		}
	} else {
		return null;
	}
}

function searchMatchesSpecies(species, search) {
	let searchWords = search.toLowerCase().trim().split(/\s+/);
	var textToSearch = species.common_name.toLowerCase();

	if(searchWords.length == 0) {
		return false;
	}

	for(var word of searchWords) {
		let index = textToSearch.indexOf(word);
		if(index == -1) {
			return false;
		} else {
			textToSearch = textToSearch.substr(index);
		}
	}

	return true;
}

function speciesFilteredBySearch(speciesList, search) {
	if(!search) {
		// If species list is short, just show all species
		if(speciesList.length < 12) {
			return speciesList;
		} else {
			return [];
		}
	}
	return _.filter(speciesList, function(species) {
		return searchMatchesSpecies(species, search);
	});
}

var sightingCounter = 0;

var SightingModel = function(params, sightingJSON) {
	var self = this;
	var survey = params.parent.survey;

	self.species = params.species;
	self.count = ko.observable(params.count || "");
	self.breeding = ko.observable(params.breeding || { code: 'none' });
	self.notes = ko.observable(params.notes || "");
	self.countHasFocus = ko.observable(false);
	self.selectedSubspecies = ko.observableArray(params.selectedSubspecies || []);
	self.media = ko.observableArray();
	self.isDefault = params.isDefault;
	self.survey = survey;
	self.id = params.id || null;
	self.orderAddedBySpecies = ko.observable(null);
	self.isSelected = ko.pureComputed(function() { return params.parent.selectedSighting() == self; });
	self.location = ko.observable(sightingJSON && sightingJSON.location);
	self.locationDescription = ko.pureComputed(function() {
		var l = self.location();
		return l ? util.formatDecimalCoordinates(l) : "Location not set";
	});

	self.title = ko.pureComputed(function() {
		var title = self.species.common_name;
		if(params.parent.multipleSightingsPerSpecies() && self.orderAddedBySpecies() !== null) {
			title += " #" + (self.orderAddedBySpecies() + 1);
		}
		return title;
	});

	self.notObserved = ko.observable(false);
	self.notObservedOption = ko.observable(params.notObservedOption);

	self.isPresent = ko.pureComputed(function() {
		if(self.notObserved()) {
			return false;
		}
		var hasDefaults = (survey.defaultSpecies() || []).length > 0;
		return !(hasDefaults && !self.count());
	});

	self.speciesImageURL = birdata.speciesImageURL(self.species.id, 'small_square');

	self.hideBreedingField = ko.pureComputed(function() {
		return (survey.program().code == 'powl' && self.species.id == 248) ||
			!_.contains(self.species.supertaxa, "class:Aves") ||
			survey.program().code == 'bnb' ||
			survey.program().code == 'bwb' ||
			survey.program().code == 'cnb' ||
			survey.program().code == 'segbc' ||
			survey.program().code == 'egnb' ||
			survey.program().code == 'rgh';
	});

	self.hideCountField = ko.pureComputed(function() {
		return survey.program().code == 'bnb' ||
			survey.program().code == 'cnb' ||
			survey.program().code == 'bwb' ||
			survey.program().code == 'egnb' ||
			(survey.program().code == 'rgh' && self.programFields().rgh.sightingType() == 'nest');
	});

	self.sightingClicked = function() {
		params.parent.selectedSighting(self);
		return true; // Allows clicks to still be handled as per usual
	}

	// ------ MEDIA UPLOAD ------

	self.mediaUploaderParams = {
		accept: ['image/gif', 'image/jpeg', 'image/png'],
		multiple: true,
		media: self.media,
		existingMedia: (params.media || [])
	};

	self.mediaUploaderViewModel = new MediaUploaderViewModel(self.mediaUploaderParams);

	// Drag n drop upload
	self.attachMediaDragEnter = function(data, evt) {
		evt.currentTarget.classList.add('draghover');
		return false;
	};

	self.attachMediaDragLeave = function(data, evt) {
		evt.currentTarget.classList.remove('draghover');
		return false;
	};

	self.attachMediaDrop = function(data, evt) {
		evt.currentTarget.classList.remove('draghover');
		self.mediaUploaderViewModel.handleDropEvent(evt);
		evt.preventDefault();
	};

	self.attachMediaPaste = function(data, evt) {
		return self.mediaUploaderViewModel.handlePasteEvent(evt);
	};

	// ------ EMBEDDED SURVEYS ------

	// For embedded surveys
	self.countOutside = ko.observable('');
	self.breedingOutside = ko.observable(null);
	self.countEmbeddedArea = ko.pureComputed(function() {
		if(self.countOutside() === 'P') {
			return 'P';
		} else {
			return ((+self.count() || 0) + (+self.countOutside() || 0)) || "P";
		}
	});

	self.breedingEmbeddedArea = ko.pureComputed(function() {
		if(!self.breedingOutside() || self.breedingOutside().code == "none") {
			return self.breeding();
		} else {
			return self.breedingOutside();
		}
	});
	// Clear breeding when count = 0
	ko.computed(function() {
		if(params.parent.isEmbedded() && !self.count()) {
			birdata.lookup().then(function(lookup) {
				self.breeding(lookup.find('breeding_activity', 'none'));
			});
		}
	});

	// ------ CORE STUFF

	self.orderAdded = sightingCounter++;

	self.summary = ko.pureComputed(function() {
		return self.species.common_name + " (" + self.count() + ", " + (self.breeding() ? "B" : "P") + ")";
	});

	self.range = ko.pureComputed(function() {
		return params.parent.rangeForSpecies(self.species);
	});

	self.rangeDescription = ko.pureComputed(function() {
		return rangeDescription(self.range(), true);
	});

	self.rangeClass = ko.pureComputed(function() {
		return "range range-" + self.range();
	});

	self.subspecies = ko.pureComputed(function() {
		return params.parent.subspeciesInRangeForSpecies(self.species);
	});

	self.notesRequired = ko.pureComputed(function() {
		if(self.species.common_name === "Swift Parrot" || self.species.common_name === "Regent Honeyeater" || self.species.common_name === "Australasian Bittern") {
			return true;
		}
	});

	self.notesPlaceholder = ko.pureComputed(function() {
		if(self.species.common_name === "Swift Parrot") {
			return "For this critically endangered species, please provide further information.  E.g. how distinguished from similar species, behaviour, foraging, were photos or other recordings made, etc.";
		} else if(self.species.common_name === "Regent Honeyeater") {
			return "For this critically endangered species, please provide further information.  E.g. how distinguished from similar species, behaviour, leg colour band combinations – if any, were photos or other recordings made, etc.";
		} else if(self.species.common_name === "Australasian Bittern") {
			return "For this endangered species, please provide further information. Describe its plumage, size, if heard, please describe the call (frequency, etc).";
		} else if(self.notesRequired()) {
			return "Please provide comment to support record, eg. Short description, how you eliminated similar species, etc.";
		} else if(params.parent.isEmbedded()) {
			return "Notes (please specify within or outside 2ha area)";
		} else {
			return survey.program().sightingNotesPlaceholder || "Notes";
		}
	});

	// ----- PROGRAM SPECIFIC FIELDS ----

	// Program specific fields are stored inside this observable, namespaced according to program.
	// e.g. self.programFields().sop.detectionMethod
	// This allows us to preserve values if the user switches between programs.
	// Templates and logic for program-specific fields should depend on this observable.
	self.programFields = ko.observable({});

	function updateProgramFields() {
		var program = survey.program();
		var programFields = self.programFields.peek();
		var fields = programFields[program.code];
		var json = sightingJSON && sightingJSON[program.code];

		if(!fields) {
			fields = program.sightingFields ? program.sightingFields(self) : [];
			programFields[program.code] = fields;

			// Load data if necessary
			if(json) {
				_.each(fields, function(f) {
					f.field.load(json);
				});
			}
		}
		self.programFields(programFields);
	}
	survey.program.subscribe(updateProgramFields);
	updateProgramFields();

	function currentProgramFields() {
		return self.programFields()[survey.program().code];
	}

	function programValidations() {
		var fields = _.values(currentProgramFields());
		return fields
			.filter(function(f) { return 'valid' in f; })
			.map(function(f) {
				var f = { ...f };
				f.validateInfo.tab = 'sightings';
				// monkey patch validation message to show species name.
				var fullError = f.fullError;
				f.fullError = function() {
					var error = fullError();
					return error && self.title() + ": " + error;
				};
				return f;
			});
	}

	// ----- WA PEST SPECIES ------

	self.showWAPestFields = ko.pureComputed(function() {
		return self.isPresent() && params.parent.withinWAPestZone() && _.contains([254, 999, 995, 269, 2503], self.species.id);
	});

	self.requireWAPestFields = ko.pureComputed(function() {
		return self.showWAPestFields() && moment(survey.startDate()).format("YYYY-MM-DD") > '2017-01-01';
	});

	self.waPestActivities = ko.observableArray([]);
	self.waPestTreeSpecies = ko.observable("");

	if(sightingJSON && sightingJSON.wa_pest) {
		self.waPestActivities(
			_.map(sightingJSON.wa_pest.activities, function(code) { return _.findWhere(params.parent.waPestActivityOptions, {code: code});}));
		self.waPestTreeSpecies(sightingJSON.wa_pest.tree_species);
	}

	// ----- BIBY -----

	self.showBibyFields = ko.pureComputed(function() {
		return survey.program().code == 'biby' && self.isPresent();
	});

	self.bibyInteraction = {
		initiatorSpecies: ko.observable(self.species),
		interactionObserved: ko.observable(false),
		aggressive: ko.observable(false),
		targetSpecies: ko.observable(),
		possibleReason: ko.observable(),
		outcome: ko.observable()
	};

	self.validateInteractionFields = function() { return self.showBibyFields() && self.bibyInteraction.interactionObserved(); };
	self.validateAgressiveFields = function() { return self.showBibyFields() && self.bibyInteraction.interactionObserved() && self.bibyInteraction.aggressive(); };

	var bibyValidations = [
		validate.field(self.bibyInteraction.targetSpecies, 'Target species', { tab: 'sightings' })
			.required().onlyIf(self.validateInteractionFields).build(),
		validate.field(self.bibyInteraction.possibleReason, 'Reason for interaction', { tab: 'sightings' })
			.required().onlyIf(self.validateAgressiveFields).build(),
		validate.field(self.bibyInteraction.outcome, 'Outcome of interaction', { tab: 'sightings' })
			.required().onlyIf(self.validateAgressiveFields).build()
	];

	self.bibySpeciesURL = ko.pureComputed(function() {
		var key = self.species.common_name;
		if(key in bibyProgram.speciesURLs) {
			return 'https://birdsinbackyards.net' + bibyProgram.speciesURLs[key];
		}
	});

	// ------ MORE CORE STUFF

	self.toJSON = function(embedded) {
		var count;

		if(params.parent.isEmbedded()) {
			if(embedded) {
				if(self.countOutside() == 'P') {
					count = 0;
				} else if(self.countOutside() == '') {
					count = (+self.count() || 0);
				} else {
					count = (+self.count() || 0) + (+self.countOutside() || 0);
				}
			} else {
				// Note: for embedded surveys, blank = absent (represented by null, filtered before submitting)
				count = (self.count() == '') ? null : (+self.count() || 0);
			}
		} else {
			count = (+self.count() || 0);
		}

		var sighting = {
			id: self.id,
			species_id: self.species.id,
			taxonomy_id: 1,
			count: count,
			breeding_activity: embedded ? self.breedingEmbeddedArea().code : self.breeding().code,
			notes: self.notes(),
			subspecies_ids: _.pluck(self.selectedSubspecies(), 'taxon_id'),
			media_uuids: _.chain(self.media())
				.filter(function(m) { return m.status() == 'complete'; })
				.map(function(m) { return m.uuid(); })
				.value()
		};

		if(self.location() && params.parent.sightingLocationsEnabled()) {
			sighting.location = { lat: self.location().lat, lon: self.location().lon };
		}

		// --- PROGRAM SPECIFIC FIELDS

		var programCode = survey.program().code;
		var programFields = self.programFields()[programCode];
		if(programFields) {
			var programFieldsJSON = {};
			_.each(programFields, function(f) {
				f.field.save(programFieldsJSON);
			});
			if(!_.isEmpty(programFieldsJSON)) {
				sighting[programCode] = programFieldsJSON;
			}
		}

		survey.program().updateSightingJSON?.(sighting, self, survey);

		// --------------

		// --- WA SPECIES
		if(self.showWAPestFields()) {
			sighting.wa_pest = {
				activities: _.pluck(self.waPestActivities(), 'code'),
				tree_species: self.waPestTreeSpecies()
			};
		}
		// --------------

		return sighting;
	};

	var validations = _.flatten([
		validate(self.countOutside, function(count) {
			if(params.parent.isEmbedded()) {
				if(!count && !self.count()) {
					return {
						brief: 'At least one count is required',
						full: 'At least one count is required for ' + self.species.common_name
					};
				}
			}
		}, { tab: 'sightings' }),
		validate(self.media, function(media) {
			if(_.some(media, function(m) {
				return m.status() != 'complete';
			})) {
				return {
					brief: 'Upload has not completed',
					full: 'Upload has not completed for ' + self.species.common_name,
				};
			}
		}, { tab: 'sightings' }),
		validate(self.notes, function(notes) {
			if(self.notesRequired() && notes.trim() == "") {
				if(self.species.common_name === "Swift Parrot" || self.species.common_name === "Regent Honeyeater" || self.species.common_name === "Australasian Bittern") {
					return {
						brief: 'Notes required for ' + self.species.common_name + ' sightings',
						full: 'Notes required for ' + self.species.common_name + ' sightings'
					};
				}
			}
		}, { tab: 'sightings' }),
		validate(self.waPestActivities, function(activities) {
			if(self.requireWAPestFields() && activities.length == 0) {
				return {
					brief: 'Activity required',
					full: 'Activity required for ' + self.species.common_name
				};
			}
		}, { tab: 'sightings' }),
		validate(self.count, function(count) {
			if(self.requireWAPestFields() && !count) {
				return {
					brief: 'Count required',
					full: 'Count is required for '+ self.species.common_name + ' (WA Pest Species)'
				};
			} else if(survey.program().requireSightingCount && !count) {
				return {
					brief: 'Count required',
					full: 'Count is required for '+ self.species.common_name
				};
			}
		}, {tab: 'sightings'}),
		// Have removed this validation so that it's possible to submit sightings outside boundary where boundary
		// is inaccurating. Leaving the code here for now in case we need to refine this later:
		// validate(self.location, function(p) {
		// 	if(p) {
		// 		if(params.parent.outsideOfSharedSite(p)) {
		// 			return self.title() + " is located outside of shared site boundary";
		// 		} else if(params.parent.distanceFromEndpoint(p) > 100000) {
		// 			return self.title() + " is located more than 100 km from start and finish point";
		// 		}
		// 	}
		// }, {tab: 'sightings'}),
		bibyValidations
	]);

	self.notes.showError(true); // Always show notes validation message if any

	self.validations = function() {
		return _.flatten([validations, programValidations()]);
	};

	self.originalJSON = sightingJSON;
};

var SightingsViewModel = function(params, componentInfo) {
	var self = this;
	var survey = params.parent.survey;

	self.survey = survey;
	self.showNextTab = params.parent.showNextTab;

	self.multipleSightingsPerSpecies = ko.pureComputed(function() {
		return ko.unwrap(survey.program().multipleSightingsPerSpecies || false);
	});

	self.breedingOptions = ko.observableArray();
	birdata.lookup("breeding_activity").then(function(options) {
		self.breedingOptions(_.filter(options, function(option) {
			return option.code != "nonspecific_breeding_activity";
		}));
	});

	self.sightingInstructions = ko.pureComputed(function() {
		return survey.program() && survey.program().sightingInstructions;
	});


	self.searchText = ko.observable("");
	self.suggestionsVisible = ko.observable(false);

	self.sightings = ko.observableArray([]);
	self.selectedSighting = ko.observable(null).extend({ objectEquals: true });

	self.selectedSuggestion = ko.observable();

	self.speciesRangeInfo = ko.observable(null);

	self.searchHasFocus = ko.observable(false);

	self.searchPlaceholder = ko.pureComputed(function() {
		if(survey.program()?.code == 'rgh') {
			return 'Search by species name to add non-nest bird sighting'
		} else {
			return 'Search by species name';
		}
	})

	// Focusing search triggers suggestions to be visible
	var focusTimer;
	self.searchHasFocus.subscribe(function(hasFocus) {
		clearTimeout(focusTimer);
		if(hasFocus) {
			self.suggestionsVisible(true);
		} else {

			focusTimer = setTimeout(function() { self.suggestionsVisible(false); }, 50)
		}
	});

	self.isEmbedded = survey.isEmbedded;

	self.speciesList = ko.observable([]);

	// Species list can depend on program
	var defaultSpeciesList = observePromise(birdata.speciesList(), []);
	self.speciesList = ko.pureComputed(function() {
		if(survey.currentProgramData().speciesList) {
			return survey.currentProgramData().speciesList();
		} else {
			return defaultSpeciesList();
		}
	});

	self.suggestions = ko.pureComputed(function() {
		return suggestionsForSearch(self.speciesList(), self.searchText());
	});

	self.tips = ko.pureComputed(function() {
		if(!self.searchHasFocus()) {
			return "<span class='key'>SHIFT</span> + <span class='key'>RETURN</span> to search";
		}
		if(self.suggestions().length > 0) {
			return "<span class='key'>⬆</span> <span class='key'>⬇</span> to choose, <span class='key'>RETURN</span> to add sighting, <span class='key'>SHIFT</span> + <span class='key'>RETURN</span> to add and edit sighting details";
		} else {
			return null;
		}
	});

	// Fetch updated species range info whenever survey point changes
	ko.computed(function() {
		if(survey.surveyPoint()) {
			birdata.speciesRangeInfo(survey.surveyPoint().lat, survey.surveyPoint().lon).then(self.speciesRangeInfo);
		} else {
			self.speciesRangeInfo(null);
		}
	})

	self.rangeForSpecies = function(species) {
		var range, rangeInfo;

		if(!species.is_range_defined) {
			return RANGE_UNKNOWN
		} else if(self.speciesRangeInfo()) {
			rangeInfo = self.speciesRangeInfo()[species.id];
			range = rangeInfo == null ? RANGE_OUT_OF_RANGE : rangeInfo.range;
		} else {
			range = null;
		}

		return range;
	};

	self.suggestions.subscribe(function() {
		if(!isSelectedSuggestionValid()) {
			self.selectedSuggestion(self.suggestions()[0]);
		}
	});

	function isSelectedSuggestionValid() {
		let selected = self.selectedSuggestion();
		return _.contains(self.suggestions(), self.selectedSuggestion());
		// return !!selected && _.any(self.suggestions(), suggestion => selected.species.id == suggestion.species.id);
	}

	self.completion = ko.pureComputed(function() {
		var s = self.selectedSuggestion();
		if(s && s.species.common_name.toLowerCase().indexOf(self.searchText().toLowerCase()) == 0) {
			return s.species.common_name;
		} else {
			return null;
		}
	});

	self.visibleCompletion = ko.pureComputed(function() {
		var c = self.completion();
		if(c) {
			return self.searchText() + c.substring(self.searchText().length);
		} else {
			return null;
		}
	});

	self.subspeciesInRangeForSpecies = function(species) {
		var rangeInfo;

		if(self.speciesRangeInfo()) {
			rangeInfo = self.speciesRangeInfo()[species.id];
			if(rangeInfo != null && rangeInfo.subspecies != null) {
				return _.chain(species.subspecies)
				.filter(function(subspecies) {
					return _.any(rangeInfo.subspecies, function(taxon_id) {
						return subspecies.taxon_id == taxon_id;
					});
				})
				.value();
			}
		}

		return [];
	};

	function suggestionForSpecies(species) {
		return {
			species: species,
			selected: () => self.selectedSuggestion()  && self.selectedSuggestion().species.id == species.id,
			range: self.rangeForSpecies(species)
		};
	}

	function suggestionsForSearch(speciesList, search) {
		// var currentIds = _.map(self.sightings(), function(s) {
		// 	return s.species.id;
		// });

		return _.chain(speciesFilteredBySearch(speciesList, search))
		.map(suggestionForSpecies)
		.sortBy(function(suggestion) {
			return suggestion.species.common_name;
		})
		.sortBy(function(suggestion) {
			return suggestion.species.common_name.toLowerCase().indexOf(search.toLowerCase());
		})
		.sortBy('range')
		// .reject(function(suggestion) {
		// 	return _.contains(currentIds, suggestion.species.id);
		// })
		.first(10)
		.each(function(suggestion) {
			suggestion.rangeDescription = rangeDescription(suggestion.range);
			suggestion.rangeClass = "range range-" + suggestion.range;
		})
		.value();
	}


	function tabComplete() {
		var search = self.searchText();
		var completion = self.completion();

		if(completion) {
			var pos = search.length;
			while(completion[pos] == " " && pos < completion.length) pos++;
			while(completion[pos] != " " && pos < completion.length) pos++;

			if(pos < completion.length) pos++; // 1 more to include a trailing space

			self.searchText(completion.substring(0, pos));
		}
	}

	function cycleSelection(up) {
		var selected = self.selectedSuggestion();
		var suggestions = self.suggestions();
		var index = _.indexOf(suggestions, selected);
		if(index == -1) {
			selected = up ? _.last(suggestions) : _.first(suggestions);
		} else {
			selected = suggestions[up ? index - 1 : index + 1];
		}
		self.selectedSuggestion(selected);
	}

	function updateSightingNumbers() {
		var speciesCounts = {};

		_.chain(self.sightings())
			.sortBy('orderAdded')
			.each(function(sighting) {
				if(sighting.isPresent()) {
					var i = speciesCounts[sighting.species.id] || 0;
					sighting.orderAddedBySpecies(i);
					speciesCounts[sighting.species.id] = i + 1;
				} else {
					sighting.orderAddedBySpecies(null);
				}
			});
	}

	function addSelectedSuggestion(editImmediately) {
		var species = self.selectedSuggestion().species;
		addSighting(species, editImmediately);
	}

	function addSighting(species, editImmediately) {
		// Add sighting if it does not already exist in the list
		var sighting = _.find(self.sightings(), function(sighting) { return sighting.species.id == species.id });
		if(!sighting || self.multipleSightingsPerSpecies()) {
			sighting = new SightingModel({
				species: species,
				parent: self
			});

			self.sightings.push(sighting);
			updateSightingNumbers();

			if(self.forceSightingLocations()) {
				self.ignoreLocationError(false);
				self.mapDialogVisible(true);
			}
		}

		self.searchText('');
		self.suggestionsVisible(false);

		setTimeout(function() {
			self.selectedSighting(sighting);
			if(editImmediately) {
				sighting.countHasFocus(true);
			}
		}, 100);

		return sighting;
	}

	self.selectedSighting.subscribe(function(sighting) {
		if(sighting) {
			scrollToSighting(sighting);
			if(self.map() && sighting.location()) {
				self.map().panTo(new google.maps.LatLng(sighting.location().lat, sighting.location().lon));
			}
		}
	});

	function scrollToSighting(sighting) {
		var scrollDiv = document.querySelector(".sightings-container");
		var sightingDiv = scrollDiv.querySelector(".sighting[data-order='" + sighting.orderAdded + "']");
		if(!sightingDiv) {
			return;
		}
		if(scrollDiv.scrollTo) {
			scrollDiv.scrollTo({
				top: sightingDiv.offsetTop,
				behavior: "smooth"
			});
		} else {
			scrollDiv.scrollTop = sightingDiv.offsetTop;
		}
	}

	document.addEventListener('keydown', function(e) {
		if(e.keyCode == 13 && e.shiftKey) {
			// SHIFT + RETURN
			if(params.parent.currentSection().id == 'sightings' && !self.searchHasFocus()) {
				self.searchHasFocus(true);
				return false;
			}
		}
		return true;
	});

	self.searchKeyPress = function(d,e) {
		switch(e.keyCode) {
			case 9: // TAB
				if(self.searchText().length == 0) {
					return true;
				} else {
					tabComplete();
					return false;
				}

			case 38: // UP
				cycleSelection(true);
				return false;

			case 40: // DOWN
				cycleSelection(false);
				return false;

			case 13: // ENTER
				if(self.selectedSuggestion()) {
					addSelectedSuggestion(e.shiftKey);
				}
				self.selectedSuggestion(null);
				break;

			case 27: // ESC
				self.suggestionsVisible(false);
				break;

			default:
				self.suggestionsVisible(true);
				break;
		}

		return true;
	};

	self.removeSighting = function(sighting) {
		if(self.selectedSighting() === sighting) {
			self.selectedSighting(null);
		}
		self.sightings.remove(sighting);
	};

	self.acceptSuggestion = function(suggestion) {
		self.selectedSuggestion(suggestion);
		addSelectedSuggestion();
	};

	// Perhaps we could just simply have put self.sightings = survey.sightings?
	ko.computed(function() {
		survey.sightings(self.sightings());
	});


	// ----- Sightings sort
	self.sortMethods = [
		{
			description: "Common Name",
			fn: function(sighting) {
				return sighting.species.common_name;
			}
		},
		{
			description: "Scientific Name",
			fn: function(sighting) {
				return sighting.species.scientific_name;
			}
		},
		{
			description: "Taxonomy",
			fn: function(sighting) {
				return sighting.species.sort;
			}
		},
		{
			description: "Order Added",
			fn: function(sighting) {
				return -sighting.orderAdded;
			}
		}
	];

	self.sortMethod = ko.observable(self.sortMethods[3]);

	self.sortedSightings = ko.pureComputed(function() {
		return _.chain(self.sightings())
		.sortBy(self.sortMethod().fn)
		.value();
	});

	// --- WA PEST SPECIES -------

	self.withinWAPestZone = ko.observable(false);
	ko.computed(function() {
		var sp = survey.surveyPoint();
		if(sp && sp.lat && sp.lon) {
			birdata.areasAtPoint(sp.lat, sp.lon).then(function(areas) {
				self.withinWAPestZone(!!_.findWhere(areas, {layer: 'wa_pest'}));
			});
		}
	});

	self.waPestActivityOptions = [{
		name: "Roosting (overnight)",
		code: "roosting"
	}, {
		name: "Perching",
		code: "perching"
	}, {
		name: "Feeding",
		code: "feeding"
	}, {
		name: "Flying over",
		code: "flying_over"
	}, {
		name: "Other",
		code: "other"
	}];

	// --- END WA PEST SPECIES ---

	// ---- BIBY ----

	self.isBiby = ko.pureComputed(function() {
		return survey.program().code == 'biby';
	});

	// This computed reads self.sightings and then updates self.sightings -> cyclic dependency.
	ko.computed(function() {
		var defaultSpecies = survey.defaultSpecies();
		var sightings = _.filter(self.sightings.peek(), function(sighting) {
			var result = _.any(defaultSpecies, function(s) { return s.id == sighting.species.id; }) || sighting.count.peek() || !sighting.isDefault;
			return result;
		});
		_.each(defaultSpecies, function(species) {
			if(!_.any(sightings, function(sighting) { return sighting.species.id == species.id; })) {
				sightings.push(new SightingModel({
					species: species,
					parent: self,
					isDefault: true
				}));
			}
		});
		self.sightings(sightings);
	});

	self.message = ko.pureComputed(function() {
		if(self.isBiby() && survey.surveyPoint()) {
			return "The most common species in your area are shown below. To add a species not in the list, use the search bar below."
		} else {
			return "";
		}
	});

	self.printURL = ko.pureComputed(function() {
		var sp = survey.surveyPoint();
		if(self.isBiby() && sp) {
			return birdata.commonSpeciesPrintURL(sp.lat, sp.lon);
		}
	});

	// Sort by Common Name by default for BIBY
	ko.computed(function() {
		if(self.isBiby()) {
			self.sortMethod(self.sortMethods[0]);
		}
	});

	self.bibyInteractionReasons = ko.observable([]);
	self.bibyOutcomes = ko.observable([]);
	birdata.lookup().then(function(lookups) {
		self.bibyInteractionReasons(lookups['biby_interaction_reason']);
		self.bibyOutcomes(lookups['biby_interaction_outcome']);
	});

	// ---- END BIBY ----

	// -- POWL
	ko.computed(function() {
		var owl = _.find(self.speciesList(), species => species.id == 248);
		if(owl) {
			var existing = _.find(self.sightings.peek(), sighting => sighting.species.id == owl.id);
			if(survey.program().code == 'powl') {
				if(existing) {
					existing.notObservedOption('No Powerful Owls seen/heard');
				} else {
					self.sightings.push(new SightingModel({
						species: owl,
						parent: self,
						notObservedOption: 'No Powerful Owls seen/heard'
					}));
				}
			} else if(existing) {
				if(existing.notObserved()) {
					self.sightings.remove(existing);
				} else {
					existing.notObservedOption(undefined);
				}
			}
		}
	});
	// -- END POWL

	self.countPlaceholder = ko.pureComputed(function() {
		return self.isBiby() || survey.program().requireSightingCount ? "" : "P";
	});

	// --- Sightings map

	self.map = ko.observable();
	self.mapOptions = ko.observable();
	self.mapCollapsed = ko.observable(true);
	self.mapDialogVisible = ko.observable(false);

	self.sightingLocationsEnabled = ko.pureComputed(function() {
		return ko.unwrap(survey.program().sightingLocations);
	});

	self.forceSightingLocations = ko.pureComputed(function() {
		return ko.unwrap(survey.program().forceSightingLocations);
	});

	self.showExpandedMap = ko.pureComputed(function() {
		return self.sightingLocationsEnabled() && (!self.mapCollapsed() || self.mapDialogVisible());
	});
	self.showCollapsedMap = ko.pureComputed(function() {
		return self.sightingLocationsEnabled() && !self.showExpandedMap();
	});

	self.clickedLocationIcon = function(sighting) {
		self.selectedSighting(sighting);
		self.mapCollapsed(!self.mapCollapsed());
	};

	self.expandMap = function() { self.mapCollapsed(false); }
	self.collapseMap = function() { self.mapCollapsed(true); }

	self.clickedMapDialogCancel = function() {
		self.removeSighting(self.selectedSighting());
		self.mapDialogVisible(false);
	}
	self.clickedMapDialogContinue = function() {
		self.mapDialogVisible(false);
	}
	self.mapDialogContinueEnabled = ko.pureComputed(function() {
		var s = self.selectedSighting();
		return !!(s && s.location() && (!self.mapLocationError() || self.ignoreLocationError()));
	});
	self.ignoreLocationError = ko.observable(false);

	self.map.subscribe(function(map) {
		if(map) {
			googleMaps.addSingleClickEvents(map);
			google.maps.event.addListener(map, 'singleclick', handleMapClick);
		}
	});

	function handleMapClick(evt) {
		var ll = evt.latLng;
		if(self.selectedSighting() && !self.selectedSighting().location()) {
			self.selectedSighting().location({ lat: ll.lat(), lon: ll.lng() });
		}
	}

	// Automatically zoom map to survey point area and show route start/finish
	var startMarker, finishMarker;
	ko.computed(function() {
		var map = self.map();
		if(map) {
			var sp = self.survey.surveyPoint();
			var route = self.survey.routeInfo();

			if(route && route.sharedSite) {
				// Setting bounds only works when map is visible
				if(params.parent.currentSection().id == 'sightings' && self.showExpandedMap()) {
					setTimeout(function() {
						var bounds = googleMaps.bounds(route.sharedSite.bounds);
						map.fitBounds(bounds);
					});
				}
			} else if(sp) {
				map.setZoom(13);
				map.setCenter(new google.maps.LatLng(sp.lat, sp.lon));
			}

			if(startMarker) { startMarker.setMap(null); }
			if(finishMarker) { finishMarker.setMap(null); }
			if(route && route.start) {
				startMarker = mapStyles.endpointMarker(route.start, true, map);
			}
			if(route && route.finish) {
				finishMarker = mapStyles.endpointMarker(route.finish, false, map);
			}
		}
	});

	var sharedSiteId = ko.pureComputed(function() {
		var route = self.survey.routeInfo();
		return route && route.sharedSite && route.sharedSite.id;
	});

	var sharedSite = flatMapComputed(function() {
		if(sharedSiteId()) {
			return observePromise(birdata.sharedSite(sharedSiteId()));
		} else {
			return ko.observable();
		}
	});

	var sitePolygons = [];

	var lastSite;
	sharedSite.subscribe(function(site) {
		// It's annoying that this is necessary.
		if(site === lastSite) { return; }
		lastSite = site;

		if(site) {
			sitePolygons.forEach(function(p) {
				p.setMap(null);
			});
			if(self.map()) {
				sitePolygons = mapFeatures.addFeatureToMap(site.geom, self.map(), mapStyles.sharedSitePolygonStyle());
				sitePolygons.forEach(function(poly) {
					googleMaps.addSingleClickEvents(poly);
					google.maps.event.addListener(poly, 'singleclick', handleMapClick);
				});
			}
		}
	});

	self.outsideOfSharedSite = function(p) {
		if(sitePolygons.length == 0) {
			return false;
		}

		sharedSite(); // create a dependency so that validation is updated whenever route changes

		p = new google.maps.LatLng(p.lat, p.lon);

		return _.all(sitePolygons, function(poly) {
			return !google.maps.geometry.poly.containsLocation(p, poly);
		});
	}

	self.distanceFromEndpoint = function(p) {
		var r = self.survey.routeInfo();
		if(window.google && google.maps && r.start && r.finish && !r.sharedSite) {
			p = new google.maps.LatLng(p.lat, p.lon);
			var a = new google.maps.LatLng(r.start.lat, r.start.lon);
			var b = new google.maps.LatLng(r.finish.lat, r.finish.lon);

			return Math.min(
				google.maps.geometry.spherical.computeDistanceBetween(p, a),
				google.maps.geometry.spherical.computeDistanceBetween(p, b)
			);
		}
	}

	self.mapLocationError = ko.pureComputed(function() {
		var sighting = self.selectedSighting();
		if(sighting &&  sighting.location()) {
			if(self.outsideOfSharedSite(sighting.location())) {
				return "Sighting is outside of site boundary.";
			} else if(self.distanceFromEndpoint(sighting.location()) > 100000) {
				return "Sighting is more than 100 km from start and finish point.";
			}
		}
	});

	self.mapOverlayText = ko.pureComputed(function() {
		var sighting = self.selectedSighting();
		if(sighting) {
			if(!sighting.location()) {
				return "Click on the map or enter sighting coordinates.";
			} else {
				return self.mapLocationError();
			}
		}
	});

	googleMaps.load().then(function() {
		var mapInitialised = false;

		// This has the effect of only loading the map when sighting locations are needed
		ko.computed(function() {
			if(!mapInitialised && ko.unwrap(survey.program().sightingLocations)) {
				mapInitialised = true;
				var center = new google.maps.LatLng(-22.50831, 136.078612);
				var zoom = 4;

				self.mapOptions(googleMaps.defaultOptions({
					center: center,
					zoom: zoom,
					fullscreenControl: false,
					zoomControl: true,
					mapTypeControl: false,
					mapTypeId: 'hybrid'
				}));
			}
		});

		function markerIcon(selected) {
			var style = mapStyles.surveyIcon(selected);
			style.opacity = selected ? 1 : 0.3;
			return style;
		}

		var markers = {};
		ko.computed(function() {
			var map = self.map();
			if(map) {
				var oldMarkers = markers;
				markers = {};
				self.sightings().forEach(function(sighting) {
					var location = sighting.location();

					if(location) {
						var key = sighting.orderAdded;
						var marker = oldMarkers[key];
						delete oldMarkers[key];

						if(marker) {
							marker.setPosition({ lat: location.lat, lng: location.lon });
						} else {
							marker = new google.maps.Marker({
								position: { lat: location.lat, lng: location.lon },
								map: map,
								title: sighting.title(),
								icon: markerIcon(),
								draggable: true
							});
							setupMarkerEvents(marker);
						}
						marker.setIcon(markerIcon(sighting.isSelected()));
						marker.setDraggable(!self.mapDialogVisible() || sighting.isSelected());
						markers[key] = marker;
						marker.sighting = sighting;
					}
				});

				for(var key in oldMarkers) {
					oldMarkers[key].setMap(null);
				}
			}
		});

		var hoverOverlay;

		function setupMarkerEvents(marker) {
			google.maps.event.addListener(marker, 'drag', function(evt) {
				marker.sighting.location({
					lat: evt.latLng.lat(),
					lon: evt.latLng.lng()
				})
			});
			google.maps.event.addListener(marker, 'click', function(evt) {
				if(self.mapDialogVisible()) {
					return;
				}
				self.selectedSighting(marker.sighting);
			});
			// google.maps.event.addListener(marker, 'mouseover', function(evt) {
			// 	if(!hoverOverlay) {
			// 		hoverOverlay = googleMaps.hoverOverlay(marker.map);
			// 	}
			// 	hoverOverlay.position(marker.getPosition());
			// 	hoverOverlay.html(marker.sighting.title());
			// 	hoverOverlay.show();
			// });
			// google.maps.event.addListener(marker, 'mouseout', function(evt) {
			// 	if(hoverOverlay) {
			// 		hoverOverlay.hide();
			// 	}
			// });
		}
	});

	// --- End sightings map

	// --- RGH raptor nest functionality ---

	self.addRaptorNest = function() {
		let species = {
			id: -1,
			common_name: 'Raptor Nest'
		};
		let sighting = addSighting(species, true);

		sighting.programFields().rgh.sightingType('nest');
	}

	// --- End RGH raptor nest functionality ---

	// Loaded sightings for existing survey if needed
	if(params.parent.loadedSurveyJSON && survey.sightings().length == 0) {
		Promise.all([
			birdata.speciesList({ supertaxa: 'all' }),
			birdata.lookup()
		]).then(function([speciesList, lookups]) {
			var json = params.parent.loadedSurveyJSON;
			var sightings = _.map(json.sightings || [], function(sightingJSON) {
				var species = sightingJSON.species ?? _.findWhere(speciesList, {id: sightingJSON.species_id});
				return new SightingModel({
					species: species,
					selectedSubspecies: _.chain(sightingJSON.subspecies || [])
						.filter(function(ssJSON) { return !ssJSON.auto_generated; })
						.map(function(ssJSON) { return _.findWhere(species.subspecies, { taxon_id: ssJSON.taxon_id }); })
						.filter(function(ss) { return ss != null; })
						.value(),
					parent: self,
					notes: sightingJSON.notes,
					breeding: lookups.find('breeding_activity', sightingJSON.breeding_activity),
					count: sightingJSON.count,
					media: sightingJSON.media_uuids,
					id: sightingJSON.id
				}, sightingJSON);
			});

			var program = survey.program();
			if(program.updateSightingsFromJSON) {
				program.updateSightingsFromJSON(json, sightings, survey);
			}

			self.sightings(sightings);
			updateSightingNumbers();
		});
	}
};

ko.components.register('survey-edit-sightings', {
	viewModel: SightingsViewModel,
	template: require('../../templates/survey-edit-sightings.html')
});

export default SightingsViewModel;
